import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, Save, Settings2 } from "lucide-react";
import { api } from "../../../lib/api";
import { LABEL_KANAL } from "./halamanPP";

// Pengaturan Price Protection = jawaban Bos no. 19–21 (dijawab 25 Sep 2026,
// "ikutin rekomendasi") sebagai bawaan (`routers/pp/pengaturan.py`), tetap bisa
// diubah tanpa bongkar kode. Hak mengubah
// dan daftar pilihan dibaca dari backend (`GET /cashback/pp/meta`):
// `can("finance_pengaturan", ...)` generik meloloskan role finance_* padahal
// backend hanya menerima superadmin / centang eksplisit (keberatan uang-06) —
// tombol yang ujungnya 403. "Uang Dealer Partner" (no. 21) hanya superadmin.
// Simpan selalu didahului Pratinjau Dampak: mengubah pemetaan kanal memindah HP
// antarprogram, dan itu harus terlihat sebelum terjadi.

const LABEL_NILAI = {
  ...LABEL_KANAL,
  tidak_ikut: "Tidak ikut program jenis baru",
  ditahan: "DITAHAN — settlement Dealer Partner ditolak",
  milik_zoa: "Milik ZOA — Pendapatan Rebate Supplier (4910), seperti ZOA GROUP",
  true: "Ya",
  false: "Tidak",
};

// Urutan baris + penjelasan (pertanyaan Bos yang menjadi asal nilainya).
// Diekspor: tes memastikan SETIAP kunci pengaturan backend punya baris di sini.
export const BARIS_PENGATURAN = [
  { kunci: "kanal_per_jenis_pelanggan", judul: "Kanal per jenis pelanggan nota",
    asal: "Keputusan Bos no. 19 (25 Sep 2026): Dealer Partner = B2B; ZOA GROUP = Umum + USER semua cabang." },
  { kunci: "cashback_unit_belum_terjual", judul: "HP yang masih di gudang saat harga turun (Support Cashback)",
    asal: "Keputusan Bos no. 20 (25 Sep 2026): tampil di bagian “Belum Terjual” halaman Cashback ZOA GROUP." },
  { kunci: "cashback_stok_boleh_klaim_tanpa_aktivasi", judul: "Stok gudang boleh diklaim cashback tanpa tanggal aktivasi",
    asal: "Keputusan Bos no. 20 (25 Sep 2026): “tetap dihitung”, jadi hak atas HP yang belum laku ikut ditagih." },
  { kunci: "kanal_unit_diretur", judul: "HP yang diretur ke toko", asal: "Retur Dealer Partner kembali ke stok ZOA (brief hal. 6)." },
  { kunci: "kanal_nota_impor_tanpa_jenis", judul: "Nota hasil impor tanpa jenis pelanggan",
    asal: "Tidak ditebak milik siapa — bawaannya tidak ikut program jenis baru." },
  { kunci: "konversi_cashback_lama_diizinkan", judul: "Program Cashback LAMA boleh dikonversi penuh ke rumus baru",
    asal: "Keputusan Bos no. 20 (25 Sep 2026): rumus Harga Lama − Harga Baru hanya untuk program BARU." },
  { kunci: "uang_dealer_partner", judul: "Pencatatan uang Support/Cashback HP Dealer Partner",
    asal: "Keputusan Bos no. 21 (25 Sep 2026): milik ZOA → Pendapatan Rebate Supplier (4910); kalau uangnya dijanjikan ke dealer, pembayaran ke dealer dicatat terpisah. Hanya akun superadmin (Bos) yang bisa mengubahnya." },
];

const labelNilai = (v) => LABEL_NILAI[String(v)] || String(v);

export default function PanelPengaturanPP({ cbBase }) {
  const [meta, setMeta] = useState(null);
  const [galat, setGalat] = useState(null);
  const [draf, setDraf] = useState(null);
  const [dampak, setDampak] = useState(null);
  const [busy, setBusy] = useState(false);
  const [muat, setMuat] = useState(0);

  useEffect(() => {
    let batal = false;
    setGalat(null);
    api.get(`${cbBase}/pp/meta`)
      .then((r) => { if (!batal) { setMeta(r.data); setDraf(JSON.parse(JSON.stringify(r.data.pengaturan))); setDampak(null); } })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.detail || "Gagal memuat Pengaturan Price Protection"); });
    return () => { batal = true; };
  }, [cbBase, muat]);

  const bolehUbah = !!meta?.boleh_ubah_pengaturan;
  const bolehUangDealer = !!meta?.boleh_ubah_uang_dealer;
  const pilihan = meta?.pilihan_pengaturan || {};

  // Hanya kunci yang BERUBAH yang dikirim (kiriman parsial — kunci lain tidak disentuh).
  const perubahan = useMemo(() => {
    if (!meta || !draf) return {};
    const out = {};
    for (const b of BARIS_PENGATURAN) {
      if (JSON.stringify(draf[b.kunci]) !== JSON.stringify(meta.pengaturan[b.kunci])) out[b.kunci] = draf[b.kunci];
    }
    return out;
  }, [meta, draf]);
  const adaPerubahan = Object.keys(perubahan).length > 0;
  const kunciDampak = dampak ? JSON.stringify(perubahan) : null;
  const dampakSegar = dampak && dampak.untuk === kunciDampak;

  const ubah = (kunci, nilai) => { setDraf((d) => ({ ...d, [kunci]: nilai })); setDampak(null); };

  const pratinjau = async () => {
    setBusy(true);
    try {
      const r = await api.put(`${cbBase}/pp/pengaturan`, perubahan, { params: { pratinjau: "1" } });
      setDampak({ ...r.data.dampak, untuk: JSON.stringify(perubahan) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghitung dampak"); }
    finally { setBusy(false); }
  };

  const simpan = async () => {
    setBusy(true);
    try {
      const r = await api.put(`${cbBase}/pp/pengaturan`, perubahan);
      toast.success(r.data.message);
      setMuat((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan"); }
    finally { setBusy(false); }
  };

  if (galat) return <p className="text-sm text-rose-700" data-testid="cb-pp-pengaturan-galat">{galat}</p>;
  if (!meta || !draf) return <p className="text-sm text-slate-500">Memuat pengaturan…</p>;

  const pilih = (kunci, nilai, onUbah, testid, mati) => (
    <select value={String(nilai)} disabled={mati} data-testid={testid}
            onChange={(e) => onUbah(e.target.value === "true" ? true : e.target.value === "false" ? false : e.target.value)}
            className="h-9 px-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-500">
      {(Array.isArray(pilihan[kunci]) ? pilihan[kunci] : []).map((v) => (
        <option key={String(v)} value={String(v)}>{labelNilai(v)}</option>
      ))}
    </select>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="cb-pp-pengaturan">
      <div className="flex items-start gap-2">
        <Settings2 className="w-5 h-5 text-emerald-600 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-900">Pengaturan Price Protection</p>
          <p className="text-xs text-slate-500">Bawaan di bawah = keputusan Bos no. 19–21 (25 Sep 2026) — tetap bisa diubah tanpa bongkar kode.
            {!bolehUbah && " Akun ini hanya bisa melihat (yang bisa mengubah: superadmin atau akun yang dicentang “Price Protection · Ubah Pengaturan”)."}</p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {BARIS_PENGATURAN.map((b) => (
          <div key={b.kunci} className="py-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`cb-pp-pengaturan-baris-${b.kunci}`}>
            <div>
              <p className="text-sm font-medium text-slate-800">{b.judul}</p>
              <p className="text-[11px] text-slate-500">{b.asal}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:justify-end">
              {b.kunci === "kanal_per_jenis_pelanggan" ? (
                (pilihan.kanal_per_jenis_pelanggan?.jenis_pelanggan || []).map((jp) => (
                  <label key={jp} className="text-xs text-slate-600 inline-flex items-center gap-1">
                    <span className="uppercase">{jp}</span>
                    <select value={draf.kanal_per_jenis_pelanggan?.[jp] || ""} disabled={!bolehUbah}
                            data-testid={`cb-pp-pengaturan-kanal-${jp}`}
                            onChange={(e) => ubah(b.kunci, { ...draf.kanal_per_jenis_pelanggan, [jp]: e.target.value })}
                            className="h-9 px-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-500">
                      {(pilihan.kanal_per_jenis_pelanggan?.nilai || []).map((v) => <option key={v} value={v}>{labelNilai(v)}</option>)}
                    </select>
                  </label>
                ))
              ) : b.kunci === "uang_dealer_partner" ? (
                // Jawaban Bos sendiri (no. 21): pilihannya hanya DIRENDER untuk akun
                // yang backend-nya menerima (superadmin) — yang lain melihat teks.
                bolehUbah && bolehUangDealer ? (
                  <select value={draf.uang_dealer_partner} data-testid="cb-pp-pengaturan-uang_dealer_partner"
                          onChange={(e) => ubah(b.kunci, e.target.value)}
                          className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
                    {(pilihan.uang_dealer_partner || []).map((v) => <option key={v} value={v}>{labelNilai(v)}</option>)}
                  </select>
                ) : (
                  <span className="text-sm font-medium text-amber-800" data-testid="cb-pp-pengaturan-uang_dealer_partner-baca">{labelNilai(draf[b.kunci])}</span>
                )
              ) : (
                pilih(b.kunci, draf[b.kunci], (v) => ubah(b.kunci, v), `cb-pp-pengaturan-${b.kunci}`, !bolehUbah)
              )}
            </div>
          </div>
        ))}
      </div>

      {dampakSegar && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1" data-testid="cb-pp-pengaturan-dampak">
          <p className="font-semibold">Dampak kalau disimpan ({dampak.diperiksa} unit anggota diperiksa):</p>
          <p>• {dampak.kanal_berubah} unit berganti kanal · {dampak.sementara_akan_pindah} pindah program sendiri di perhitungan berikutnya
            · {dampak.terkunci_jadi_kanal_berbeda} sudah diklaim/dipindah → jadi “kanal berbeda” (perlu Pindahkan manual).</p>
          {(dampak.per_program || []).slice(0, 10).map((p) => (
            <p key={p.program_id}>• {p.program_code}: {p.kanal_berubah} unit berganti kanal</p>
          ))}
          <p className="text-amber-700">Unit yang belum ditempatkan di program mana pun tidak dihitung — mereka ditempatkan menurut pengaturan baru.</p>
        </div>
      )}

      {bolehUbah && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={pratinjau} disabled={busy || !adaPerubahan} data-testid="cb-pp-pengaturan-pratinjau"
                  className="h-9 px-3 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-slate-50">
            <Eye className="w-4 h-4" /> Pratinjau Dampak
          </button>
          <button onClick={simpan} disabled={busy || !adaPerubahan || !dampakSegar} data-testid="cb-pp-pengaturan-simpan"
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> Simpan Pengaturan
          </button>
        </div>
      )}
    </div>
  );
}
